// 配信先(会社)管理。lineChannelAccessTokenは一覧・編集フォームいずれにも平文表示しない(設定済み/未設定のみ)。
import { useEffect, useState } from "react";
import { apiGet, apiPost, ApiError } from "../../lib/api";
import type { Company } from "../../types";

const emptyForm = { id: "", name: "", equipment: "", lineChannelAccessToken: "", active: true };

export function Companies() {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [toast, setToast] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => apiGet<{ companies: Company[] }>("/api/companies").then((r) => setCompanies(r.companies));
  useEffect(() => {
    load();
  }, []);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2400);
  };

  const startEdit = (c: Company) => {
    setEditingId(c.id);
    // ★lineChannelAccessTokenは空欄開始。未入力のまま保存すれば既存値を維持する(サーバー側仕様)。
    setForm({ id: c.id, name: c.name, equipment: c.equipment, lineChannelAccessToken: "", active: c.active });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      showToast("会社・拠点名を入力してください");
      return;
    }
    const id = editingId || form.id.trim().toLowerCase() || form.name.trim().toLowerCase().replace(/[^a-z0-9-]/g, "-");
    setSaving(true);
    try {
      await apiPost("/api/companies", {
        id,
        name: form.name.trim(),
        equipment: form.equipment.trim(),
        lineChannelAccessToken: form.lineChannelAccessToken.trim(), // 空なら現状維持(サーバー側で判定)
        active: form.active,
        isNew: !editingId, // 新規作成時のみ。既存IDと衝突していればサーバーが409で拒否する
      });
      showToast("会社を保存しました");
      cancelEdit();
      load();
    } catch (e) {
      showToast(e instanceof ApiError ? e.message : "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid">
      <div className="card">
        <h2>
          <span className="ic">🏢</span>配信先(各社ポータル＋LINE)
        </h2>
        <p className="hint" style={{ marginTop: 0 }}>
          各社は「ポータル(/c/会社ID)＋LINE公式アカウント」の1組。チャネルアクセストークンは一覧・編集画面いずれにも表示されません(設定済み/未設定のみ)。
        </p>
        {!companies.length && <div className="empty">まだ会社が登録されていません。</div>}
        {companies.map((c) => (
          <div key={c.id} className={`dest${!c.active ? "" : " on"}`}>
            <div className="d-main">
              <div className="d-name">
                {c.name} <span style={{ fontWeight: 400, color: "var(--muted)", fontSize: 11 }}>/c/{c.id}</span>
              </div>
              <div style={{ fontSize: 11.5, color: "var(--muted)" }}>
                {c.lineConfigured ? "💬 LINE設定済み" : "💬(LINE未設定)"}・{c.equipment || "機材メモなし"}・
                {c.active ? "有効" : "無効"}
              </div>
            </div>
            <button className="btn ghost sm" onClick={() => startEdit(c)}>
              編集
            </button>
          </div>
        ))}
      </div>

      <div className="card">
        <h2>
          <span className="ic">➕</span>会社を{editingId ? "編集" : "追加"}
        </h2>
        <div className="row">
          <div style={{ flex: 2, minWidth: 150 }}>
            <label className="f">会社・拠点名</label>
            <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div style={{ flex: 1, minWidth: 110 }}>
            <label className="f">会社ID(URL用)</label>
            <input
              type="text"
              value={form.id}
              disabled={!!editingId}
              placeholder="kaisha-a"
              onChange={(e) => setForm({ ...form, id: e.target.value })}
            />
          </div>
        </div>
        <label className="f">採用機材(メモ)</label>
        <input type="text" value={form.equipment} onChange={(e) => setForm({ ...form, equipment: e.target.value })} />
        <label className="f">LINEチャネルアクセストークン {editingId && "(変更する場合のみ入力。空欄なら現状維持)"}</label>
        <input
          type="text"
          value={form.lineChannelAccessToken}
          placeholder="LINE公式アカウントマネージャー → Messaging API設定 → チャネルアクセストークン"
          onChange={(e) => setForm({ ...form, lineChannelAccessToken: e.target.value })}
        />
        <div className="hint">
          LINE公式アカウントマネージャー(https://manager.line.biz/)でMessaging APIを有効化し、発行されたトークンを貼り付けてください。薬局スタッフには公式アカウントのQRコードを配布し「友だち追加」してもらう運用です。
        </div>
        <label className="row" style={{ marginTop: 10, fontSize: 13 }}>
          <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
          有効(配信対象に含める)
        </label>
        <div className="row" style={{ marginTop: 14 }}>
          <button className="btn" onClick={handleSave} disabled={saving}>
            保存
          </button>
          {editingId && (
            <button className="btn ghost sm" onClick={cancelEdit}>
              キャンセル
            </button>
          )}
        </div>
      </div>
      {toast && <div className="toast" style={{ opacity: 1 }}>{toast}</div>}
    </div>
  );
}
